/**
 * 2026-06-20 — Customer Invoices page.
 *
 * Lists tax invoices that have been auto-issued against the user's
 * payments (B2C topups + subscription cycles, and any B2B invoices on
 * the user's org). Each row offers a download — the backend returns
 * a 1-hour presigned S3 URL the browser fetches directly.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, Download, FileText, RefreshCw, AlertCircle,
} from "lucide-react";
import { listMyInvoices, getMyInvoicePdf } from "../../api/payments.api";


function inrPaise(p) {
  return `₹${(p / 100).toLocaleString("en-IN", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}


export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await listMyInvoices();
      setInvoices(res?.data?.invoices || []);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        "Could not load invoices. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const download = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await getMyInvoicePdf(inv.id);
      const url = res?.data?.url;
      if (url) {
        // open in a new tab so the user keeps the listing context
        window.open(url, "_blank", "noopener");
      } else {
        setError("Invoice PDF is not ready yet. Try again in a minute.");
      }
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        "Invoice PDF unavailable. Try again later."
      );
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link
            to="/account/wallet"
            className="text-text-secondary hover:text-text-primary flex items-center gap-1 text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Wallet
          </Link>
          <span className="text-text-secondary/40">/</span>
          <h1 className="text-2xl font-semibold text-text-primary">Tax invoices</h1>
        </div>
        <button
          onClick={load}
          className="text-text-secondary hover:text-text-primary flex items-center gap-1.5 px-2 py-1 rounded hover:bg-bg-secondary/40 text-sm"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <p className="text-sm text-text-secondary mb-4">
        GST-valid tax invoices for your payments. Issued automatically by
        Zenminds Sporting Technologies Pvt Ltd on every successful charge.
      </p>

      {error && (
        <div className="mb-4 bg-rose-500/10 border border-rose-500/40 rounded-md p-3 text-sm text-rose-200 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <div className="bg-bg-primary border border-border-default rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-text-secondary">
            Loading invoices…
          </div>
        ) : invoices.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-text-secondary">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No invoices yet. They appear here automatically after every
            successful topup or subscription charge.
          </div>
        ) : (
          <div className="divide-y divide-border-default">
            {invoices.map((inv) => {
              const isBusy = busyId === inv.id;
              return (
                <div
                  key={inv.id}
                  className="px-5 py-3 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-text-secondary" />
                      <span className="text-sm font-semibold text-text-primary">
                        {inv.number}
                      </span>
                      <span className="text-xs text-text-secondary">
                        · {inv.date}
                      </span>
                      {inv.status !== "ISSUED" && (
                        <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">
                          {inv.status}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-text-secondary truncate mt-0.5">
                      {inv.line_description}
                    </div>
                    <div className="text-[11px] text-text-secondary/80 mt-0.5">
                      Taxable {inrPaise(inv.taxable_paise)}
                      {" · "}
                      GST {inrPaise(inv.cgst_paise + inv.sgst_paise + inv.igst_paise)}
                      {" · "}
                      <span className="text-text-primary font-medium">
                        Total {inrPaise(inv.total_paise)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => download(inv)}
                    disabled={isBusy || !inv.has_pdf}
                    title={inv.has_pdf ? "Download PDF" : "PDF being prepared"}
                    className="shrink-0 inline-flex items-center gap-1.5 text-sm bg-bg-secondary hover:bg-bg-secondary/70 border border-border-default text-text-primary px-3 py-1.5 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    {isBusy ? "Opening…" : "PDF"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="mt-4 text-xs text-text-secondary">
        Invoices are also emailed to your account email at the time of
        issuance. If you can&apos;t find one, contact{" "}
        <a className="underline" href="mailto:a.manivannan@sportingzenminds.com">support</a>.
      </p>
    </div>
  );
}
